import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Button,
  Checkbox,
  Input,
  InputTag,
  Message,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "@arco-design/web-react";
import { IconDelete, IconPlus, IconEdit } from "@arco-design/web-react/icon";
import { FetchApi, PostApi, OverwriteApi, DeleteApi } from "../../Api/AuthenticatedApi";
import useOrgFeatures from "../../utils/useOrgFeatures";

const { Text } = Typography;

const TYPE_OPTIONS = [
  { label: "Text", value: "text" },
  { label: "Number", value: "number" },
  { label: "Date", value: "date" },
  { label: "Yes / No", value: "boolean" },
  { label: "Dropdown", value: "select" },
  { label: "Email", value: "email" },
  { label: "Phone", value: "phone" },
  { label: "ZIP", value: "zip" },
  { label: "City", value: "city" },
  { label: "State", value: "state" },
  { label: "Country", value: "country" },
];
const TYPE_LABEL = Object.fromEntries(TYPE_OPTIONS.map((t) => [t.value, t.label]));

export default function CustomFieldSettings() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [entityType, setEntityType] = useState("contact");
  const { companies } = useOrgFeatures();
  // Companies turned off while their tab was open: back to contacts.
  useEffect(() => {
    if (!companies && entityType === "company") setEntityType("contact");
  }, [companies, entityType]);
  const [fields, setFields] = useState([]);
  const [refresh, setRefresh] = useState(true);

  const [types, setTypes] = useState([]);

  const [editing, setEditing] = useState(null); // {} = new, def = edit, null = closed
  const [label, setLabel] = useState("");
  const [fieldType, setFieldType] = useState("text");
  const [options, setOptions] = useState([]);
  const [required, setRequired] = useState(false);
  const [archived, setArchived] = useState(false);
  const [appliesToTypeIds, setAppliesToTypeIds] = useState([]);
  const [saving, setSaving] = useState(false);

  // Types manager
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");

  useEffect(() => { setRefresh(true); }, [entityType]);

  const fetchTypes = () =>
    FetchApi(getAccessTokenSilently, "/api/record-types/?entity_type=" + entityType,
      (data) => setTypes(data || []), () => {}, () => {});

  useEffect(() => {
    if (!refresh) return;
    FetchApi(getAccessTokenSilently, "/api/custom-fields/?entity_type=" + entityType,
      (data) => { setFields(data || []); setRefresh(false); },
      () => navigate("/login"),
      () => { setRefresh(false); Message.error("Failed to load custom fields"); });
    fetchTypes();
  }, [getAccessTokenSilently, navigate, refresh, entityType]); // eslint-disable-line react-hooks/exhaustive-deps

  const typeName = (id) => (types.find((t) => t.id === id) || {}).name || "?";

  const addType = () => {
    if (!newTypeName.trim()) return;
    PostApi(getAccessTokenSilently, "/api/record-types/",
      () => { setNewTypeName(""); fetchTypes(); Message.success("Type added"); },
      () => navigate("/login"),
      (err) => Message.error((err && err.message) || "Failed to add type"),
      { entity_type: entityType, name: newTypeName.trim() });
  };
  const makeDefault = (t) => {
    OverwriteApi(getAccessTokenSilently, "/api/record-types/" + t.id,
      () => fetchTypes(), () => navigate("/login"), () => Message.error("Failed to update type"),
      { name: t.name, is_default: true });
  };
  const removeType = (t) => {
    DeleteApi(getAccessTokenSilently, "/api/record-types/" + t.id,
      () => fetchTypes(), () => navigate("/login"), () => Message.error("Failed to delete type"), {});
  };

  const openNew = () => {
    setEditing({}); setLabel(""); setFieldType("text"); setOptions([]); setRequired(false); setArchived(false); setAppliesToTypeIds([]);
  };
  const openEdit = (d) => {
    setEditing(d); setLabel(d.label || ""); setFieldType(d.field_type || "text");
    setOptions(d.options || []); setRequired(!!d.required); setArchived(!!d.archived);
    setAppliesToTypeIds(d.applies_to_type_ids || []);
  };
  const closeEditor = () => { setEditing(null); setSaving(false); };

  const save = () => {
    if (!label.trim()) { Message.warning("Give the field a name"); return; }
    setSaving(true);
    const body = {
      entity_type: entityType,
      label: label.trim(),
      field_type: fieldType,
      options: fieldType === "select" ? options : [],
      required,
      archived,
      applies_to_type_ids: appliesToTypeIds,
    };
    const onOk = () => { Message.success("Field saved"); closeEditor(); setRefresh(true); };
    const onErr = (err) => { setSaving(false); Message.error((err && err.message) || "Failed to save field"); };
    if (editing && editing.id) {
      OverwriteApi(getAccessTokenSilently, "/api/custom-fields/" + editing.id, onOk, () => navigate("/login"), onErr, body);
    } else {
      PostApi(getAccessTokenSilently, "/api/custom-fields/", onOk, () => navigate("/login"), onErr, body);
    }
  };

  const remove = (d) => {
    Modal.confirm({
      title: "Delete field",
      content: `Delete "${d.label}"? Existing values on records are left in place but hidden.`,
      okButtonProps: { status: "danger" },
      onOk: () => new Promise((resolve) => {
        DeleteApi(getAccessTokenSilently, "/api/custom-fields/" + d.id,
          () => { Message.success("Field deleted"); setRefresh(true); resolve(); },
          () => navigate("/login"),
          () => { Message.error("Failed to delete field"); resolve(); },
          {});
      }),
    });
  };

  const columns = [
    {
      title: "Field",
      dataIndex: "label",
      render: (l, d) => (
        <span>
          <Text style={{ fontWeight: 500 }}>{l}</Text>
          {d.required && <Tag size="small" color="orange" style={{ marginLeft: 6 }}>required</Tag>}
          {d.archived && <Tag size="small" color="gray" style={{ marginLeft: 6 }}>archived</Tag>}
        </span>
      ),
    },
    { title: "Type", dataIndex: "field_type", render: (t) => TYPE_LABEL[t] || t },
    {
      title: "Applies to",
      dataIndex: "applies_to_type_ids",
      render: (ids) => (!ids || ids.length === 0
        ? <Text type="secondary">All types</Text>
        : ids.map((id) => <Tag key={id} size="small" style={{ marginRight: 4 }}>{typeName(id)}</Tag>)),
    },
    {
      title: "",
      width: 96,
      render: (_, d) => (
        <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
          <Button size="small" type="text" shape="circle" icon={<IconEdit />} onClick={() => openEdit(d)} />
          <Button size="small" type="text" shape="circle" icon={<IconDelete />} onClick={() => remove(d)} />
        </div>
      ),
    },
  ];

  return (
    <div>
      <Text type="secondary">
        Add your own fields to contacts{companies ? " and companies" : ""}. They appear on the record editor and (for contacts)
        will be filterable in segments.
      </Text>

      <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 12 }}>
        {companies && (
          <Radio.Group
            type="button"
            value={entityType}
            onChange={setEntityType}
            options={[{ label: "Contacts", value: "contact" }, { label: "Companies", value: "company" }]}
          />
        )}
        <Button size="small" type="text" onClick={() => setTypeModalOpen(true)}>
          Manage types{types.length ? ` (${types.length})` : ""}
        </Button>
      </div>

      <Table
        style={{ marginTop: 16 }}
        size="small"
        columns={columns}
        data={fields}
        rowKey="id"
        pagination={false}
        border={false}
        noDataElement={<span style={{ color: "var(--color-text-3)" }}>No custom fields for {entityType === "contact" ? "contacts" : "companies"} yet.</span>}
      />

      <div style={{ marginTop: 16 }}>
        <Button type="primary" icon={<IconPlus />} onClick={openNew}>New field</Button>
      </div>

      <Modal
        title={editing && editing.id ? "Edit field" : "New field"}
        visible={!!editing}
        onCancel={closeEditor}
        footer={
          <Space>
            <Button onClick={closeEditor}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={save}>Save</Button>
          </Space>
        }
        unmountOnExit
      >
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          <div>
            <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Name</Text></div>
            <Input placeholder="e.g. Industry" value={label} onChange={setLabel} />
          </div>
          <div>
            <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Type</Text></div>
            <Select
              style={{ width: "100%" }}
              value={fieldType}
              onChange={setFieldType}
              options={TYPE_OPTIONS}
              disabled={!!(editing && editing.id)}
            />
          </div>
          {fieldType === "select" && (
            <div>
              <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Options</Text></div>
              <InputTag placeholder="Type an option and press Enter" value={options} onChange={setOptions} />
            </div>
          )}
          {types.length > 0 && (
            <div>
              <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Applies to</Text></div>
              <Select
                mode="multiple"
                allowClear
                style={{ width: "100%" }}
                placeholder="All types"
                value={appliesToTypeIds}
                onChange={setAppliesToTypeIds}
                options={types.map((t) => ({ label: t.name, value: t.id }))}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>Leave empty to show on every record.</Text>
            </div>
          )}
          <Space size={24}>
            <Checkbox checked={required} onChange={setRequired}>Required</Checkbox>
            {editing && editing.id && <Checkbox checked={archived} onChange={setArchived}>Archived (hide)</Checkbox>}
          </Space>
        </Space>
      </Modal>

      <Modal
        title={`${entityType === "contact" ? "Contact" : "Company"} types`}
        visible={typeModalOpen}
        onCancel={() => setTypeModalOpen(false)}
        footer={<Button onClick={() => setTypeModalOpen(false)}>Done</Button>}
        unmountOnExit
      >
        <Text type="secondary">
          Types let different {entityType === "contact" ? "contacts" : "companies"} carry different fields.
          The default type is applied to new records.
        </Text>
        <Table
          style={{ marginTop: 12 }}
          size="small"
          rowKey="id"
          pagination={false}
          border={false}
          data={types}
          noDataElement={<span style={{ color: "var(--color-text-3)" }}>No types yet — add one below.</span>}
          columns={[
            {
              title: "Name",
              dataIndex: "name",
              render: (n, t) => <span>{n}{t.is_default && <Tag size="small" color="arcoblue" style={{ marginLeft: 6 }}>default</Tag>}</span>,
            },
            {
              title: "",
              width: 150,
              render: (_, t) => (
                <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                  {!t.is_default && <Button size="mini" type="text" onClick={() => makeDefault(t)}>Make default</Button>}
                  <Button size="mini" type="text" shape="circle" icon={<IconDelete />} onClick={() => removeType(t)} />
                </div>
              ),
            },
          ]}
        />
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <Input placeholder="New type name" value={newTypeName} onChange={setNewTypeName} onPressEnter={addType} />
          <Button type="primary" onClick={addType}>Add</Button>
        </div>
      </Modal>
    </div>
  );
}
